import { isDrawableByPdfMake } from "../pdf-image.js";
import type { TDocumentDefinitions, Content, TableCell } from "pdfmake/interfaces.js";
import type { DocumentBuilder, DocumentSnapshot } from "../document-engine.js";
import { documentEngine } from "../document-engine.js";
import type { LpDocumentModel } from "../../../domain/ndt/lp-report-domain.js";
import {
  HEADER_PAGE_MARGIN_TOP,
  INSPECTION_REPORT_HEADER_STYLES,
  buildInspectionReportHeader
} from "./inspection-report-header.js";
import { fitBox } from "./image-size.js";

/**
 * Caixas máximas das imagens. `fit` preserva a proporção e nunca amplia além
 * do tamanho natural (fitBox): a foto horizontal usa boa largura com altura
 * limitada; a vertical fica limitada em altura — nenhuma ocupa a página só
 * para preencher, e mais de uma cabe por página quando há espaço.
 */
const CROQUI_MAX: [number, number] = [497, 260];
const PHOTO_MAX: [number, number] = [400, 240];
/** Comentário até este tamanho viaja dentro do bloco indivisível da foto; mais longo quebra normalmente. */
const SHORT_COMMENT_CHARS = 400;

/** A4 (595.28pt) menos as margens laterais de 40pt — nada pode passar disto. */
const USABLE_WIDTH = 515;

/** Paleta documental aprovada na IMAGEM 02: fundo branco, azul institucional. */
const BLUE = "#1B4F8F";
const BAND_FILL = "#DCE8F7";
const HEAD_FILL = "#EAF1FA";
const BORDER = "#C9D3E0";
const LABEL = "#5A6572";
const TEXT = "#1F2933";
const MUTED = "#6B7683";

const LAUDO_FILL: Record<string, { fill: string; color: string }> = {
  APROVADO: { fill: "#D9F2DF", color: "#1E6B34" },
  REPROVADO: { fill: "#F9D9D9", color: "#9B1C1C" },
  EXAME_COMPLEMENTAR: { fill: "#FBEFD0", color: "#8A5A00" },
  EM_ELABORACAO: { fill: "#E9ECEF", color: "#4B5563" }
};

/** Grade com todas as linhas finas — a aparência de formulário técnico da IMAGEM 02. */
const GRID_LAYOUT = {
  hLineWidth: () => 0.5,
  vLineWidth: () => 0.5,
  hLineColor: () => BORDER,
  vLineColor: () => BORDER,
  paddingTop: () => 3,
  paddingBottom: () => 3,
  paddingLeft: () => 5,
  paddingRight: () => 5
};

/** Caixa da imagem: moldura fina com pouco respiro — a imagem já é o conteúdo. */
const IMAGE_BOX_LAYOUT = {
  hLineWidth: () => 0.6,
  vLineWidth: () => 0.6,
  hLineColor: () => BORDER,
  vLineColor: () => BORDER,
  paddingTop: () => 4,
  paddingBottom: () => 4,
  paddingLeft: () => 4,
  paddingRight: () => 4
};

/** Caixa simples (observações, termo, croqui). */
const BOX_LAYOUT = {
  hLineWidth: () => 0.6,
  vLineWidth: () => 0.6,
  hLineColor: () => BORDER,
  vLineColor: () => BORDER,
  paddingTop: () => 6,
  paddingBottom: () => 6,
  paddingLeft: () => 8,
  paddingRight: () => 8
};

/**
 * "RELATÓRIO DE INSPEÇÃO POR LÍQUIDO PENETRANTE (LP)".
 *
 * Consome o LpDocumentModel — o MESMO objeto que o Preview recebe, porque o
 * Preview renderiza este PDF. Paginação totalmente dinâmica: nenhum
 * pageBreak forçado; só o cabeçalho e o rodapé nativos se repetem. Cada
 * campo é opcional-seguro — croqui, foto ou assinatura ausentes nunca
 * quebram a geração.
 */
export class LpBuilder implements DocumentBuilder {
  build(snapshot: DocumentSnapshot): TDocumentDefinitions {
    const model = (snapshot.data || {}) as LpDocumentModel;

    return {
      pageSize: "A4",
      pageOrientation: "portrait",
      // Mesmas margens laterais/inferior do ME; a linha de identidade sob o logo e
      // o QR maior (72pt) pedem 12pt a mais no topo para o conteúdo não encostar.
      pageMargins: [40, HEADER_PAGE_MARGIN_TOP + 12, 40, 44],
      header: () => this.buildHeader(model),
      // Título de seção nunca fica sozinho no fim da página: se nada do que o
      // segue coube na mesma página, a quebra acontece ANTES do título. É a
      // única regra de quebra — o resto é o fluxo do conteúdo. (API do
      // pdfmake 0.3: nodeInfo + consultas; o texto interno da própria faixa
      // também é "nó seguinte" e é descontado pelo estilo "band".)
      pageBreakBefore: (currentNode: any, nodeQueries: any) =>
        currentNode.headlineLevel === 1 &&
        nodeQueries.getFollowingNodesOnPage().filter((n: any) => n.style !== "band").length === 0,
      footer: (currentPage: number, pageCount: number) => ({
        margin: [40, 6, 40, 0],
        stack: [
          { canvas: [{ type: "line", x1: 0, y1: 0, x2: USABLE_WIDTH, y2: 0, lineWidth: 0.5, lineColor: BORDER }] },
          {
            margin: [0, 5, 0, 0],
            columns: [
              { text: "INSPECT IA — Sistema de Inspeção e Integridade Mecânica", style: "footerText" },
              { text: `${model.reportNumber || "—"}  ·  Rev. ${model.revision || "00"}`, style: "footerText", alignment: "center" },
              { text: `Página ${currentPage} de ${pageCount}`, style: "footerText", alignment: "right" }
            ]
          }
        ]
      }),
      content: [
        this.sectionBand("01", "IDENTIFICAÇÃO"),
        this.buildIdentification(model),
        this.sectionBand("02", "DADOS DO ENSAIO"),
        this.buildTestData(model),
        this.buildConsumables(model),
        this.sectionBand("03", "RESULTADOS DA INSPEÇÃO DAS JUNTAS SOLDADAS / ÁREAS"),
        this.buildResults(model),
        // 04 e 05 em sequência vertical: o croqui ocupa a largura útil sozinho
        // e as fotografias começam depois que ele termina — sem coordenada
        // fixa, o pdfmake empilha e o 05 desce o quanto o croqui exigir.
        this.sectionBand("04", "CROQUI"),
        this.buildCroquiBox(model.croqui, USABLE_WIDTH),
        this.sectionBand("05", "EVIDÊNCIAS FOTOGRÁFICAS"),
        this.buildPhotoBlocks(model.photos, USABLE_WIDTH),
        this.sectionBand("06", "OBSERVAÇÕES E PARECER FINAL"),
        this.buildTextBox(model.observations),
        this.sectionBand("07", "TERMO DE RESPONSABILIDADE"),
        this.buildTextBox(model.responsibilityTerm),
        this.sectionBand("08", "SIGNATÁRIOS"),
        this.buildSignatories(model)
      ],
      styles: {
        // Cabeçalho compartilhado com o ME (inspection-report-header.ts) —
        // mesma estrutura; no LP as fontes sobem: título em maior destaque,
        // INSPECT IA como destaque secundário, identidade menor mas legível,
        // bloco documental ≈ 30% maior. Estilos são do documento: o ME segue
        // com os valores dele.
        ...INSPECTION_REPORT_HEADER_STYLES,
        headerLogo: { ...INSPECTION_REPORT_HEADER_STYLES.headerLogo, fontSize: 15 },
        headerSub: { ...INSPECTION_REPORT_HEADER_STYLES.headerSub, fontSize: 8 },
        headerDocTitle: { ...INSPECTION_REPORT_HEADER_STYLES.headerDocTitle, fontSize: 17, lineHeight: 1.1, margin: [0, 2, 0, 0] },
        headerField: { ...INSPECTION_REPORT_HEADER_STYLES.headerField, fontSize: 9 },
        footerText: { fontSize: 6.8, color: MUTED },
        band: { fontSize: 8.5, bold: true, color: BLUE },
        subTitle: { fontSize: 7.5, bold: true, color: BLUE },
        label: { fontSize: 7, color: LABEL },
        value: { fontSize: 7.2, color: TEXT },
        th: { fontSize: 6.8, bold: true, color: BLUE },
        td: { fontSize: 7, color: TEXT },
        photoNumber: { fontSize: 6.8, bold: true, color: TEXT },
        photoCaption: { fontSize: 6.5, color: MUTED },
        sigRole: { fontSize: 6.5, bold: true, color: TEXT },
        sigName: { fontSize: 7.5, bold: true, color: TEXT },
        sigMeta: { fontSize: 6.8, color: MUTED },
        boxText: { fontSize: 7.2, color: TEXT, lineHeight: 1.25 },
        empty: { fontSize: 7, color: MUTED, italics: true }
      },
      defaultStyle: { fontSize: 7.5, color: TEXT }
    };
  }

  // ---------------------------------------------------------------- HEADER

  /** O MESMO cabeçalho do ME — não existe um modelo exclusivo do LP. */
  private buildHeader(model: LpDocumentModel): Content {
    const fields = [
      { label: "Rel. cliente nº", value: model.clientReportNumber || "-" },
      { label: "Rel. INSPECT IA nº", value: model.reportNumber || "-" },
      { label: "Revisão", value: model.revision || "00" },
      { label: "Data de emissão", value: model.issueDate || "-" },
      { label: "Ordem de Serviço", value: model.osNumber || "-" }
    ];
    // Sub-OS só quando existe.
    if (model.subOsNumber) fields.push({ label: "Sub-OS", value: model.subOsNumber });

    return buildInspectionReportHeader({
      title: "RELATÓRIO DE ENSAIO POR\nLÍQUIDO PENETRANTE",
      subtitle: "Sistema de Inspeção e Integridade Mecânica",
      qrReference: model.qrPayload || `INSPECT-IA|LP|${model.reportNumber || ""}`,
      // QR ≈ 33% maior que o do ME (54 → 72), proporção 1:1, mesmo lugar.
      qrSize: 72,
      // Bloco documental com fonte 9 pede um pouco mais de largura.
      fieldsWidth: 186,
      fields,
      unitArea: model.identification?.unitArea || "-",
      tag: model.identification?.equipmentTag || "-",
      visual: model.visual ?? null
    });
  }

  // -------------------------------------------------------------- SECTIONS

  private sectionBand(number: string, title: string): Content {
    return {
      table: {
        widths: ["*"],
        body: [[{ text: `${number}. ${title}`, style: "band", fillColor: BAND_FILL, margin: [4, 2, 0, 2], border: [true, false, false, false] }]]
      },
      layout: {
        hLineWidth: () => 0,
        vLineWidth: (i: number) => (i === 0 ? 2 : 0),
        vLineColor: () => BLUE,
        paddingTop: () => 2,
        paddingBottom: () => 2,
        paddingLeft: () => 4,
        paddingRight: () => 4
      },
      margin: [0, 10, 0, 4],
      headlineLevel: 1
    } as Content;
  }

  /** Grade de pares rótulo/valor em duas colunas, como as seções 01 e 02 da IMAGEM 02. */
  /**
   * Um par por coluna e linha. Quando uma linha tem só o par da esquerda, o
   * valor ocupa o resto da linha — nenhuma célula fica vazia.
   */
  private pairGrid(left: Array<[string, string]>, right: Array<[string, string]>): Content {
    const rows = Math.max(left.length, right.length);
    const body: TableCell[][] = [];
    for (let i = 0; i < rows; i++) {
      const l = left[i];
      const r = right[i];
      if (l && !r) {
        body.push([
          { text: l[0], style: "label" },
          { text: this.value(l[1]), style: "value", colSpan: 3 }, {}, {}
        ]);
        continue;
      }
      body.push([
        { text: l ? l[0] : "", style: "label" },
        { text: l ? this.value(l[1]) : "", style: "value" },
        { text: r ? r[0] : "", style: "label" },
        { text: r ? this.value(r[1]) : "", style: "value" }
      ]);
    }
    return {
      table: { widths: [88, "*", 96, "*"], body },
      layout: GRID_LAYOUT
    };
  }

  private buildIdentification(model: LpDocumentModel): Content {
    const id = model.identification || ({} as LpDocumentModel["identification"]);
    // Tipo de Equipamento vem do cadastro real do Equipment (equipmentType).
    // Sub-OS só quando existe — sem ela a OS ocupa a linha inteira.
    return this.pairGrid(
      [
        ["Cliente:", id.client],
        ["Projeto / Contrato:", id.projectContract],
        ["Unidade / Área:", id.unitArea],
        ["Ordem de Serviço:", id.osNumber]
      ],
      [
        ["Equipamento / TAG:", id.equipmentTag],
        ["Tipo de Equipamento:", id.equipmentDescription],
        ["Data do Ensaio:", id.testDate],
        ...(id.subOsNumber ? [["Sub-OS:", id.subOsNumber] as [string, string]] : [])
      ]
    );
  }

  private buildTestData(model: LpDocumentModel): Content {
    const t = model.testData || ({} as LpDocumentModel["testData"]);
    return this.pairGrid(
      [
        ["Procedimento / Revisão:", t.procedure],
        ["Norma de Referência:", t.referenceNorm],
        ["Critério de Aceitação:", t.acceptanceCriteria],
        ["Material Base:", t.baseMaterial],
        ["Tempo de Penetração:", t.penetrationTime]
      ],
      [
        ["Família / Técnica:", t.penetrantFamily],
        ["Tipo de Iluminação:", t.illuminationType],
        ["Intensidade Medida:", t.illuminationIntensity],
        ["Temperatura do Ensaio:", t.testTemperature]
      ]
    );
  }

  private buildConsumables(model: LpDocumentModel): Content {
    const list = Array.isArray(model.consumables) ? model.consumables : [];
    const body: TableCell[][] = [[
      { text: "Tipo", style: "th", fillColor: HEAD_FILL },
      { text: "Marca", style: "th", fillColor: HEAD_FILL },
      { text: "Produto", style: "th", fillColor: HEAD_FILL },
      { text: "Lote", style: "th", fillColor: HEAD_FILL },
      { text: "Validade", style: "th", fillColor: HEAD_FILL }
    ]];

    if (list.length === 0) {
      body.push([{ text: "—", style: "empty", colSpan: 5, alignment: "center" }, {}, {}, {}, {}]);
    } else {
      for (const c of list) {
        body.push([
          { text: this.value(c.typeLabel), style: "td" },
          { text: this.value(c.brand), style: "td" },
          { text: this.value(c.product), style: "td" },
          { text: this.value(c.lot), style: "td" },
          // Validade vencida em vermelho, vigente em verde — sem inventar data.
          { text: this.value(c.validity), style: "td", color: c.validity ? (c.expired ? "#9B1C1C" : "#1E6B34") : TEXT, bold: !!c.validity }
        ]);
      }
    }

    return {
      stack: [
        { text: "CONSUMÍVEIS UTILIZADOS", style: "subTitle", margin: [0, 6, 0, 3] },
        {
          table: { headerRows: 1, widths: [80, 120, 100, 80, "*"], body, dontBreakRows: true },
          layout: GRID_LAYOUT
        }
      ]
    };
  }

  private buildResults(model: LpDocumentModel): Content {
    const rows = Array.isArray(model.results) ? model.results : [];
    const body: TableCell[][] = [[
      { text: "Item", style: "th", fillColor: HEAD_FILL, alignment: "center" },
      { text: "Junta / Elemento", style: "th", fillColor: HEAD_FILL },
      { text: "Localização", style: "th", fillColor: HEAD_FILL },
      { text: "Extensão (mm)", style: "th", fillColor: HEAD_FILL, alignment: "center" },
      { text: "Descrição das Descontinuidades / Achados", style: "th", fillColor: HEAD_FILL },
      { text: "Laudo Técnico", style: "th", fillColor: HEAD_FILL, alignment: "center" }
    ]];

    if (rows.length === 0) {
      body.push([{ text: "Nenhum item registrado.", style: "empty", colSpan: 6, alignment: "center" }, {}, {}, {}, {}, {}]);
    } else {
      for (const r of rows) {
        const paint = LAUDO_FILL[r.laudo] ?? null;
        body.push([
          { text: this.value(r.item), style: "td", alignment: "center" },
          { text: this.value(r.element), style: "td" },
          { text: this.value(r.location), style: "td" },
          { text: this.value(r.extension), style: "td", alignment: "center" },
          { text: this.value(r.description), style: "td" },
          paint
            ? { text: this.value(r.laudoLabel), style: "td", alignment: "center", bold: true, fillColor: paint.fill, color: paint.color }
            : { text: this.value(r.laudoLabel), style: "td", alignment: "center" }
        ]);
      }
    }

    // Larguras de conteúdo + 6 colunas × 2 × 5pt de padding cabem em 515pt;
    // a descrição recebe o restante ("*").
    return {
      table: { headerRows: 1, widths: [26, 70, 74, 52, "*", 84], body, dontBreakRows: true },
      layout: GRID_LAYOUT
    };
  }

  private buildCroquiBox(croqui: LpDocumentModel["croqui"], width: number): Content {
    if (!croqui || !croqui.image) {
      return {
        table: {
          widths: ["*"],
          body: [[{ text: "Nenhum croqui anexado.", style: "empty", alignment: "center" }]]
        },
        layout: BOX_LAYOUT
      };
    }
    // fit preserva a proporção — a imagem nunca é esticada, deformada nem
    // ampliada além do tamanho natural.
    return {
      table: {
        widths: ["*"],
        body: [[{ image: croqui.image, fit: fitBox(croqui.image, Math.min(width - 18, CROQUI_MAX[0]), CROQUI_MAX[1]), alignment: "center" }]]
      },
      layout: BOX_LAYOUT
    };
  }

  /**
   * Evidências UMA ABAIXO DA OUTRA: cada fotografia é um bloco próprio —
   * "FOTO XX", a imagem em boa parte da largura útil (proporção preservada
   * por fit), e Componente / Localização / Comentário exatamente como
   * salvos. Título + imagem + componente + localização não se separam na
   * paginação (o bloco desce inteiro quando não cabe); o comentário quebra
   * normalmente. Nenhuma coordenada fixa — sem página em branco.
   */
  private buildPhotoBlocks(photos: LpDocumentModel["photos"], width: number): Content {
    const list = Array.isArray(photos) ? photos.filter(p => !!p.image) : [];
    if (list.length === 0) {
      return {
        table: {
          widths: ["*"],
          body: [[{ text: "Nenhuma evidência fotográfica anexada.", style: "empty", alignment: "center" }]]
        },
        layout: BOX_LAYOUT
      };
    }

    const captionRow = (label: string, value: string): TableCell[] => [
      { text: label, style: "label" },
      { text: this.value(value), style: "value" }
    ];

    return {
      stack: list.map((photo, index) => {
        const comment = { table: { widths: [88, "*"], body: [captionRow("Comentário:", photo.comment)] }, layout: GRID_LAYOUT };
        const shortComment = (photo.comment || "").length <= SHORT_COMMENT_CHARS;
        // Bloco indivisível: FOTO XX + imagem + componente/localização (+ o
        // comentário quando curto). Não cabendo, desce inteiro para a página
        // seguinte; a foto anterior fica onde está. Comentário longo fica fora
        // do bloco e quebra normalmente.
        const block = {
          unbreakable: true,
          stack: [
            { text: photo.number, style: "photoNumber", margin: [0, 0, 0, 3] },
            {
              table: {
                widths: ["*"],
                body: [[{ image: photo.image, fit: fitBox(photo.image, Math.min(width - 20, PHOTO_MAX[0]), PHOTO_MAX[1]), alignment: "center" }]]
              },
              layout: IMAGE_BOX_LAYOUT
            },
            {
              table: { widths: [88, "*"], body: [captionRow("Componente:", photo.item), captionRow("Localização:", photo.location)] },
              layout: GRID_LAYOUT
            },
            ...(shortComment ? [comment] : [])
          ]
        };
        return {
          margin: [0, index === 0 ? 0 : 10, 0, 0],
          stack: (shortComment ? [block] : [block, comment]) as Content[]
        };
      })
    };
  }

  private buildTextBox(text: string | null | undefined): Content {
    const value = typeof text === "string" ? text.trim() : "";
    return {
      table: {
        widths: ["*"],
        body: [[value ? { text: value, style: "boxText" } : { text: "—", style: "empty" }]]
      },
      layout: BOX_LAYOUT
    };
  }

  private buildSignatories(model: LpDocumentModel): Content {
    const list = Array.isArray(model.signatories) ? model.signatories : [];
    const roles = ["INSPETOR", "PLH", "FISCAL"] as const;
    const labels: Record<string, string> = { INSPETOR: "INSPETOR DE EQUIPAMENTOS", PLH: "ENGENHEIRO / PLH", FISCAL: "FISCAL DO CONTRATO" };

    const cells: TableCell[] = roles.map(role => {
      const s = list.find(x => x.role === role);
      return {
        stack: [
          { text: labels[role]!, style: "sigRole", alignment: "center" },
          this.signatureImage(s?.signatureImage),
          { text: this.value(s?.name), style: "sigName", alignment: "center" },
          { text: this.value(s?.functionTitle), style: "sigMeta", alignment: "center" },
          { text: s?.credential ? s.credential : "—", style: "sigMeta", alignment: "center" },
          { text: `Data: ${this.value(s?.date)}`, style: "sigMeta", alignment: "center", margin: [0, 2, 0, 0] }
        ],
        margin: [0, 4, 0, 4]
      };
    });

    return {
      table: { widths: ["*", "*", "*"], body: [cells], dontBreakRows: true },
      layout: {
        hLineWidth: () => 0.6,
        vLineWidth: () => 0.6,
        hLineColor: () => BORDER,
        vLineColor: () => BORDER,
        paddingTop: () => 4,
        paddingBottom: () => 4,
        paddingLeft: () => 6,
        paddingRight: () => 6
      }
    };
  }

  /** A imagem REAL da assinatura cadastrada; sem ela, um espaço para assinar. */
  private signatureImage(image: string | null | undefined): Content {
    if (isDrawableByPdfMake(image)) {
      return { image, fit: [120, 38], alignment: "center", margin: [0, 6, 0, 4] };
    }
    return {
      stack: [
        { text: " ", margin: [0, 14, 0, 0] },
        { canvas: [{ type: "line", x1: 20, y1: 0, x2: 130, y2: 0, lineWidth: 0.5, lineColor: MUTED }] },
        { text: " ", margin: [0, 2, 0, 0] }
      ]
    };
  }

  // --------------------------------------------------------------- HELPERS

  /** "—" para o que estiver vazio — um campo opcional ausente nunca deixa célula em branco. */
  private value(input: unknown): string {
    if (input === null || input === undefined) return "—";
    const text = String(input).trim();
    return text.length > 0 ? text : "—";
  }
}

documentEngine.registerBuilder("LP", new LpBuilder());
